import { createContext, useContext, useState, ReactNode, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Trash2, X } from "lucide-react";
import {
  useClient,
  useClientLeads,
  useClientBudgets,
  useClientProjectsForClient,
  useUpsertClient,
  useDeleteClient,
} from "@/hooks/useClients";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { formatBRL } from "@/lib/format";

interface ClientDrawerContextValue {
  openClient: (id: string) => void;
  close: () => void;
}

const ClientDrawerContext = createContext<ClientDrawerContextValue | null>(null);

export function useClientDrawer() {
  const ctx = useContext(ClientDrawerContext);
  if (!ctx) throw new Error("useClientDrawer must be used within ClientDrawerProvider");
  return ctx;
}

export function ClientDrawerProvider({ children }: { children: ReactNode }) {
  const [openId, setOpenId] = useState<string | null>(null);

  const openClient = useCallback((id: string) => setOpenId(id), []);
  const close = useCallback(() => setOpenId(null), []);

  return (
    <ClientDrawerContext.Provider value={{ openClient, close }}>
      {children}
      <ClientDrawer id={openId} onClose={close} />
    </ClientDrawerContext.Provider>
  );
}

const fmtBRL = (v: number) => formatBRL(v);

const segTrigger =
  "flex-1 rounded-lg px-3 py-[7px] text-xs font-semibold text-muted-foreground data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm";

type TimelineEntry = {
  key: string;
  at: string;
  dot: string;
  title: string;
  meta: string;
  to?: string;
};

function ClientDrawer({ id, onClose }: { id: string | null; onClose: () => void }) {
  const navigate = useNavigate();
  const { data: client, isLoading } = useClient(id);
  const { data: leads } = useClientLeads(id);
  const { data: budgets } = useClientBudgets(id);
  const { data: projects } = useClientProjectsForClient(id);
  const upsert = useUpsertClient();
  const deleteClient = useDeleteClient();

  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", notes: "" });

  const startEdit = () => {
    if (!client) return;
    setForm({
      name: client.name,
      email: client.email,
      phone: client.phone ?? "",
      company: client.company ?? "",
      notes: client.notes ?? "",
    });
    setEditing(true);
  };

  const handleSave = async () => {
    if (!client) return;
    await upsert.mutateAsync({
      id: client.id,
      name: form.name,
      email: form.email,
      phone: form.phone || null,
      company: form.company || null,
      notes: form.notes || null,
    });
    setEditing(false);
  };

  const timeline: TimelineEntry[] = [
    ...(leads ?? []).map((l) => ({
      key: `l-${l.id}`,
      at: l.created_at,
      dot: "bg-primary",
      title: `Lead recebido · ${l.status}`,
      meta: `${formatDistanceToNow(new Date(l.created_at), { addSuffix: true, locale: ptBR })}${l.message ? ` · ${l.message}` : ""}`,
      to: `/admin/leads/${l.id}`,
    })),
    ...(budgets ?? []).map((b) => ({
      key: `b-${b.id}`,
      at: b.updated_at,
      dot: "bg-warn",
      title: `Proposta ${b.title} · ${fmtBRL(Number(b.net_total))}`,
      meta: `${b.status} · ${formatDistanceToNow(new Date(b.updated_at), { addSuffix: true, locale: ptBR })}`,
      to: b.lead_id ? `/admin/leads/${b.lead_id}?tab=proposta` : undefined,
    })),
    ...(projects ?? []).map((p) => ({
      key: `p-${p.id}`,
      at: p.created_at,
      dot: "bg-ok",
      title: `Projeto ${p.title}`,
      meta: `${p.phase} · ${formatDistanceToNow(new Date(p.created_at), { addSuffix: true, locale: ptBR })}`,
      to: p.lead_id ? `/admin/leads/${p.lead_id}?tab=entrega` : undefined,
    })),
  ].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());

  const primaryLeadId = leads?.[0]?.id ?? null;
  const contactLine = client
    ? [client.email, client.phone, client.company].filter(Boolean).join(" · ")
    : "";

  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 shadow-elegant sm:max-w-[520px] [&>button]:hidden"
      >
        {isLoading || !client ? (
          <div className="flex h-40 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <SheetHeader className="space-y-0 border-b border-border px-6 pb-[18px] pt-[22px] text-left">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <SheetTitle className="truncate text-lg font-bold tracking-[-0.02em]">
                    {client.name}
                  </SheetTitle>
                  <p className="mt-[5px] truncate text-xs text-muted-foreground">{contactLine}</p>
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 shrink-0 rounded-[9px] text-muted-foreground"
                  onClick={onClose}
                  aria-label="Fechar"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </SheetHeader>

            {editing ? (
              <div className="flex-1 space-y-3 overflow-y-auto px-6 py-5">
                <div className="space-y-1.5">
                  <Label htmlFor="client-nome">Nome</Label>
                  <Input
                    id="client-nome"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="client-email">Email</Label>
                  <Input
                    id="client-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="client-telefone">Telefone</Label>
                  <Input
                    id="client-telefone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="client-empresa-cnpj">Empresa / CNPJ</Label>
                  <Input
                    id="client-empresa-cnpj"
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="client-notas-internas">Notas internas</Label>
                  <Textarea
                    id="client-notas-internas"
                    rows={4}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  />
                </div>
              </div>
            ) : (
              <Tabs defaultValue="timeline" className="flex min-h-0 flex-1 flex-col">
                <div className="px-6 pt-4">
                  <TabsList className="grid h-auto w-full grid-cols-3 gap-[3px] rounded-[10px] bg-muted p-[3px]">
                    <TabsTrigger value="timeline" className={segTrigger}>
                      Linha do tempo
                    </TabsTrigger>
                    <TabsTrigger value="budgets" className={segTrigger}>
                      Orçamentos · {budgets?.length ?? 0}
                    </TabsTrigger>
                    <TabsTrigger value="projects" className={segTrigger}>
                      Projetos · {projects?.length ?? 0}
                    </TabsTrigger>
                  </TabsList>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-[22px]">
                  <TabsContent value="timeline" className="mt-0">
                    {timeline.length === 0 && (
                      <p className="py-6 text-center text-sm text-muted-foreground">
                        Sem atividades ainda.
                      </p>
                    )}
                    {timeline.map((t, idx) => {
                      const body = (
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-[13px] font-semibold">{t.title}</div>
                          <div className="mt-0.5 truncate text-[11.5px] text-muted-foreground">
                            {t.meta}
                          </div>
                        </div>
                      );
                      return (
                        <div key={t.key} className="flex gap-[13px] pb-[18px]">
                          <div className="flex flex-col items-center gap-1">
                            <span
                              className={`mt-[5px] h-[9px] w-[9px] shrink-0 rounded-full ${t.dot}`}
                            />
                            {idx < timeline.length - 1 && (
                              <span className="w-px flex-1 bg-border" />
                            )}
                          </div>
                          {t.to ? (
                            <Link
                              to={t.to}
                              onClick={onClose}
                              className="min-w-0 flex-1 hover:text-primary"
                            >
                              {body}
                            </Link>
                          ) : (
                            body
                          )}
                        </div>
                      );
                    })}
                    {client.notes && (
                      <div className="mt-2 rounded-[13px] border border-border bg-muted px-[18px] py-4">
                        <div className="text-xs font-bold text-muted-foreground">
                          Notas internas
                        </div>
                        <p className="mt-2 whitespace-pre-wrap text-[12.5px] leading-[1.6] text-muted-foreground">
                          {client.notes}
                        </p>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="budgets" className="mt-0 space-y-2">
                    {(budgets?.length ?? 0) === 0 && (
                      <p className="py-6 text-center text-sm text-muted-foreground">
                        Nenhum orçamento.
                      </p>
                    )}
                    {budgets?.map((b) => (
                      <Link
                        key={b.id}
                        to={
                          b.lead_id ? `/admin/leads/${b.lead_id}?tab=proposta` : "/admin/pipeline"
                        }
                        onClick={onClose}
                        className="flex items-center justify-between gap-3 rounded-[11px] border border-border px-3.5 py-3 hover:bg-muted/60"
                      >
                        <span className="truncate text-[13px] font-medium">{b.title}</span>
                        <div className="flex shrink-0 items-center gap-2">
                          <span className="text-xs font-bold">{fmtBRL(Number(b.net_total))}</span>
                          <span className="inline-flex h-[23px] items-center rounded-[7px] bg-muted px-2 text-[11px] font-bold text-muted-foreground">
                            {b.status}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </TabsContent>

                  <TabsContent value="projects" className="mt-0 space-y-2">
                    {(projects?.length ?? 0) === 0 && (
                      <p className="py-6 text-center text-sm text-muted-foreground">
                        Nenhum projeto.
                      </p>
                    )}
                    {projects?.map((p) => (
                      <Link
                        key={p.id}
                        to={p.lead_id ? `/admin/leads/${p.lead_id}?tab=entrega` : "/admin/projetos"}
                        onClick={onClose}
                        className="flex items-center justify-between gap-3 rounded-[11px] border border-border px-3.5 py-3 hover:bg-muted/60"
                      >
                        <span className="truncate text-[13px] font-medium">{p.title}</span>
                        <span className="inline-flex h-[23px] items-center rounded-[7px] bg-muted px-2 text-[11px] font-bold text-muted-foreground">
                          {p.phase}
                        </span>
                      </Link>
                    ))}
                  </TabsContent>
                </div>
              </Tabs>
            )}

            <div className="flex justify-end gap-[9px] border-t border-border px-6 py-4">
              {editing ? (
                <>
                  <Button
                    variant="outline"
                    className="h-[38px] rounded-[10px] text-[12.5px] font-semibold"
                    onClick={() => setEditing(false)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    className="h-[38px] rounded-[10px] text-[12.5px] font-semibold"
                    onClick={handleSave}
                    disabled={upsert.isPending}
                  >
                    Salvar
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    className="h-[38px] rounded-[10px] text-[12.5px] font-semibold"
                    onClick={startEdit}
                  >
                    Editar cliente
                  </Button>
                  <Button
                    className="h-[38px] rounded-[10px] text-[12.5px] font-semibold"
                    disabled={!primaryLeadId}
                    onClick={() => {
                      if (!primaryLeadId) return;
                      onClose();
                      navigate(`/admin/leads/${primaryLeadId}`);
                    }}
                  >
                    Abrir lead
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-[38px] w-[38px] rounded-[10px] text-bad hover:text-bad"
                    aria-label="Excluir cliente"
                    title="Excluir cliente"
                    onClick={() => setConfirmDelete(true)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>
          </>
        )}
      </SheetContent>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="max-w-[430px] rounded-[18px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[17px] tracking-[-0.02em]">
              Excluir cliente &quot;{client?.name}&quot;?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[13px] leading-[1.6]">
              {(leads?.length ?? 0) > 0 || (projects?.length ?? 0) > 0 || (budgets?.length ?? 0) > 0
                ? `${leads?.length ?? 0} lead(s), ${projects?.length ?? 0} projeto(s) e ${budgets?.length ?? 0} orçamento(s) ficarão sem cliente vinculado, mas não serão apagados.`
                : "Este cliente não tem leads, projetos ou orçamentos vinculados."}{" "}
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-[38px] rounded-[10px] text-[12.5px] font-semibold">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              className="h-[38px] rounded-[10px] bg-bad text-[12.5px] font-semibold text-bad-foreground hover:bg-bad/90"
              onClick={() => {
                if (!client) return;
                deleteClient.mutate(client.id, {
                  onSuccess: () => {
                    setConfirmDelete(false);
                    onClose();
                  },
                });
              }}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}
